<script setup lang="ts">
import ClovyrHost from '@clovyr/bed/assets/images/clovyrhost-lockup.svg';
import { useMatomo } from '@clovyr/bed/composables/useMatomo';
import type { UserSubscription, UserSubscriptionItem } from '@clovyr/pollen';
import type { PaymentMethod } from '@clovyr/pollen/subscription';

import Modal from '@/components/elements/Modal.vue';
import { useAppResume } from '@/composables/useAppResume';
import { useAppStatus } from '@/composables/useAppStatus';

import CardPaymentButton from './CardPaymentButton.vue';
import CoinPaymentButton from './CoinPaymentButton.vue';

interface AppSubscriptionProps {
  id: string;
  name: string;
  subscription?: UserSubscription;
  subscriptionItem?: UserSubscriptionItem;
}

const props = defineProps<AppSubscriptionProps>();
const deploymentID = toRef(props, 'id');

// Upgrade subscription - same as clicking 'upgrade' on app detail
const handleSubscriptionAddPayment = async (paymentMethod: PaymentMethod) => {
  if (!props.id) {
    // not an app subscription trial which can be upgraded
    return;
  }
  // matomo track event Payment Initiated
  const matomo = useMatomo();
  matomo.trackEvent('Payment', 'selectPayment', paymentMethod);
  const { doUpgrade } = useAppStatus(deploymentID);
  if (
    await doUpgrade(deploymentID.value, props.subscription!, props.subscriptionItem!, paymentMethod)
  ) {
    void useAppResume(deploymentID).wakeUpInstance();
  }
};
</script>

<template>
  <Modal class="sub-modal" theme="transparent" size="narrow" @close="$emit('close')" max-on-mobile>
    <template #body>
      <div class="upgrade-heading">
        <h2>Upgrade</h2>
        <ImageMedia :filename="ClovyrHost" />
      </div>
      <div class="upgrade-body">
        To upgrade hosting for {{ name }}, select a payment method below.
        <br />
        Your account will be charged at the end of the free trial.
      </div>

      <div class="payment-options">
        <CardPaymentButton @click="handleSubscriptionAddPayment('stripe')" />
        <CoinPaymentButton @click="handleSubscriptionAddPayment('opennode')" />
      </div>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

.upgrade-heading {
  display: flex;
  align-items: center;
  margin-top: 25%;
  margin-bottom: 1rem;

  @include breakpoints.media-breakpoint-up(sm) {
    margin-top: 1rem;
  }

  h2 {
    color: #ffffff;
    margin-right: 0.75rem;
  }
}
.upgrade-body {
  font-size: 19px;
  margin-bottom: 3rem;

  @include breakpoints.media-breakpoint-up(sm) {
    margin-bottom: 2rem;
  }
}

.payment-options {
  width: fit-content;
  margin: auto;

  @include breakpoints.media-breakpoint-up(md) {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1rem;
  }
}
</style>
